import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Customer } from '@/hooks/useCustomers';
import { BulkUploadHistoryModal } from './BulkUploadHistoryModal';

interface BulkUploadModalProps {
  customer: Customer;
  onClose: () => void;
  onUploadComplete?: (files: UploadedFile[]) => void;
}

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'error';
  progress: number;
  error?: string;
  result?: {
    bill_id?: string;
    amount?: number;
    confidence?: number;
  };
}

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
const MAX_FILES = 20;
const ACCEPTED_TYPES = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png']
};

export const BulkUploadModal: React.FC<BulkUploadModalProps> = ({ 
  customer, 
  onClose, 
  onUploadComplete 
}) => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [fileObjects, setFileObjects] = useState<Map<string, File>>(new Map());
  const [isUploading, setIsUploading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
    // Handle rejected files
    rejectedFiles.forEach(({ file, errors }) => {
      errors.forEach((error: any) => {
        if (error.code === 'file-too-large') {
          toast.error(`${file.name} is too large. Maximum size is 25MB.`);
        } else if (error.code === 'file-invalid-type') {
          toast.error(`${file.name} is not a supported file type. Use PDF, JPG, or PNG.`);
        }
      });
    });

    // Check total file limit
    if (files.length + acceptedFiles.length > MAX_FILES) {
      toast.error(`Cannot upload more than ${MAX_FILES} files at once.`);
      return;
    }

    // Add accepted files
    const newFiles: UploadedFile[] = acceptedFiles.map(file => {
      const id = `${Date.now()}-${Math.random()}`;
      return {
        id,
        name: file.name,
        size: file.size,
        type: file.type,
        status: 'pending',
        progress: 0
      };
    });

    // Store file objects separately
    const newFileObjects = new Map(fileObjects);
    acceptedFiles.forEach((file, index) => {
      newFileObjects.set(newFiles[index].id, file);
    });
    setFileObjects(newFileObjects);
    setFiles(prev => [...prev, ...newFiles]);
  }, [files.length, fileObjects]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxSize: MAX_FILE_SIZE,
    maxFiles: MAX_FILES - files.length,
    disabled: isUploading
  });

  const removeFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
    setFileObjects(prev => {
      const newMap = new Map(prev);
      newMap.delete(fileId);
      return newMap;
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type: string) => {
    if (type.includes('pdf')) return '📄';
    if (type.includes('image')) return '🖼️';
    return '📎';
  };

  const getStatusColor = (status: UploadedFile['status']) => {
    switch (status) {
      case 'pending': return 'secondary';
      case 'uploading': return 'default';
      case 'processing': return 'default';
      case 'completed': return 'default';
      case 'error': return 'destructive';
      default: return 'secondary';
    }
  };

  const getStatusIcon = (status: UploadedFile['status']) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'uploading': return '📤';
      case 'processing': return '⚙️';
      case 'completed': return '✅';
      case 'error': return '❌';
      default: return '⏳';
    }
  };

  const uploadFile = async (file: UploadedFile, actualFile: File) => {
    try {
      // Update status to uploading
      setFiles(prev => prev.map(f => 
        f.id === file.id 
          ? { ...f, status: 'uploading', progress: 0 }
          : f
      ));

      // Upload to Supabase Storage
      const fileName = `${customer.id}/${Date.now()}_${actualFile.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('bills')
        .upload(fileName, actualFile);

      // Simulate progress for UI (since Supabase doesn't support progress callbacks)
      for (let progress = 0; progress <= 80; progress += 20) {
        setFiles(prev => prev.map(f => 
          f.id === file.id 
            ? { ...f, progress }
            : f
        ));
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      if (uploadError) throw uploadError;

      // Update progress to 80% (upload complete)
      setFiles(prev => prev.map(f => 
        f.id === file.id 
          ? { ...f, progress: 80 }
          : f
      ));

      // Create bill entry in queue
      const { data: billData, error: billError } = await supabase
        .from('bill_upload_queue')
        .insert({
          user_id: customer.id,
          file_path: uploadData.path,
          file_name: actualFile.name,
          file_size: actualFile.size,
          source_type: 'bulk_upload',
          source_address: 'bulk_upload',
          processing_status: 'pending',
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (billError) throw billError;

      // Update progress to 90% (queue entry created)
      setFiles(prev => prev.map(f => 
        f.id === file.id 
          ? { ...f, progress: 90, status: 'processing' }
          : f
      ));

      // Log upload event
      await supabase.from('ingestion_events').insert({
        user_id: customer.id,
        source_type: 'bulk_upload',
        source_ref: billData.id,
        event_type: 'file_uploaded',
        status: 'success',
        details: {
          file_name: actualFile.name,
          file_size: actualFile.size,
          bill_id: billData.id,
          file_path: uploadData.path
        }
      });

      // Trigger AI processing
      const parserResponse = await supabase.functions.invoke('multi-tier-bill-parser', {
        body: { 
          queue_item_id: billData.id
        }
      });

      // Complete the file
      if (parserResponse.data?.success) {
        setFiles(prev => prev.map(f => 
          f.id === file.id 
            ? { 
                ...f, 
                status: 'completed', 
                progress: 100,
                result: {
                  bill_id: billData.id,
                  amount: 0, // Will be updated by AI processing
                  confidence: 0 // Will be updated by AI processing
                }
              }
            : f
        ));

        // Log processing started
        await supabase.from('ingestion_events').insert({
          user_id: customer.id,
          source_type: 'bulk_upload',
          source_ref: billData.id,
          event_type: 'processing_started',
          status: 'success',
          details: {
            bill_id: billData.id,
            parser_job_id: parserResponse.data.job_id
          }
        });
      } else {
        throw new Error('AI processing failed to start');
      }

    } catch (error) {
      console.error(`Upload failed for ${file.name}:`, error);
      
      setFiles(prev => prev.map(f => 
        f.id === file.id 
          ? { ...f, status: 'error', error: error.message || 'Upload failed' }
          : f
      ));
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setIsUploading(true);
    toast.success(`Starting upload of ${files.length} files for ${customer.name}`);

    try {
      // Process files sequentially to avoid overwhelming the system
      for (const file of files) {
        if (file.status === 'pending') {
          const actualFile = fileObjects.get(file.id);
          if (actualFile) {
            await uploadFile(file, actualFile);
          } else {
            setFiles(prev => prev.map(f => 
              f.id === file.id 
                ? { ...f, status: 'error', error: 'File object not found' }
                : f
            ));
          }
        }
      }
      
      const completedFiles = files.filter(f => f.status === 'completed');
      const errorFiles = files.filter(f => f.status === 'error');
      
      if (completedFiles.length > 0) {
        toast.success(`Successfully processed ${completedFiles.length} files`);
      }
      if (errorFiles.length > 0) {
        toast.error(`Failed to process ${errorFiles.length} files`);
      }

      onUploadComplete?.(files);
    } catch (error) {
      console.error('Bulk upload failed:', error);
      toast.error('Bulk upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const totalFiles = files.length;
  const completedFiles = files.filter(f => f.status === 'completed').length;
  const errorFiles = files.filter(f => f.status === 'error').length;
  const pendingFiles = files.filter(f => f.status === 'pending').length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full m-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Bulk Upload Bills</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Upload bills for {customer.name} • Max {MAX_FILES} files, 25MB each
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            ✕
          </Button>
        </div>

        {/* Upload Area */}
        <div className="p-6">
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragActive 
                ? 'border-primary bg-primary/5' 
                : isUploading
                ? 'border-muted bg-muted/20 cursor-not-allowed'
                : 'border-border hover:border-primary hover:bg-primary/5 cursor-pointer'
            }`}
          >
            <input {...getInputProps()} />
            <div className="text-4xl mb-4">📁</div>
            {isDragActive ? (
              <p className="text-primary">Drop the files here...</p>
            ) : isUploading ? (
              <p className="text-muted-foreground">Upload in progress...</p>
            ) : (
              <>
                <p className="text-foreground font-medium">Drag & drop files here, or click to browse</p>
                <p className="text-sm text-muted-foreground mt-2">
                  PDF, JPG, PNG up to 25MB • {MAX_FILES - files.length} files remaining
                </p>
              </>
            )}
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-medium text-foreground">
                  Files to Upload ({totalFiles})
                </h3>
                <div className="flex gap-4 text-sm">
                  {pendingFiles > 0 && <span className="text-muted-foreground">⏳ {pendingFiles} pending</span>}
                  {completedFiles > 0 && <span className="text-green-600">✅ {completedFiles} completed</span>}
                  {errorFiles > 0 && <span className="text-destructive">❌ {errorFiles} failed</span>}
                </div>
              </div>

              <div className="max-h-64 overflow-y-auto space-y-2 border border-border rounded-lg">
                {files.map(file => (
                  <div key={file.id} className="flex items-center gap-4 p-3 border-b border-border last:border-b-0">
                    <div className="text-xl">{getFileIcon(file.type)}</div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground truncate">{file.name}</span>
                        <Badge variant={getStatusColor(file.status)} className="text-xs">
                          {getStatusIcon(file.status)} {file.status}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatFileSize(file.size)}
                        {file.result && (
                          <span className="ml-2">
                            • ${file.result.amount} • {file.result.confidence}% confidence
                          </span>
                        )}
                        {file.error && (
                          <span className="ml-2 text-destructive">• {file.error}</span>
                        )}
                      </div>
                      {file.status === 'uploading' && (
                        <Progress value={file.progress} className="mt-2 h-1" />
                      )}
                    </div>

                    {!isUploading && file.status === 'pending' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(file.id)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        ✕
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-border bg-muted/30">
          <div className="text-sm text-muted-foreground">
            {files.length > 0 && !isUploading && (
              <span>Ready to upload {files.length} files</span>
            )}
            {isUploading && (
              <span>Processing files... This may take a few minutes</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowHistory(true)}
              disabled={isUploading}
            >
              📋 View History
            </Button>
            <Button 
              variant="outline" 
              onClick={onClose}
              disabled={isUploading}
            >
              {isUploading ? 'Cancel' : 'Close'}
            </Button>
            {files.length > 0 && !isUploading && (
              <Button onClick={handleUpload}>
                📤 Upload {files.length} Files
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {/* History Modal */}
      {showHistory && (
        <BulkUploadHistoryModal
          customer={customer}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
};